import type { EnemyIntention } from '../domain/types';

/** Explicit enemy loot tier — never inferred from name/art. */
export type EnemyClassification = 'ordinary' | 'elite' | 'boss';

export interface EnemyTemplate {
  id: string;
  name: string;
  maxHp: number;
  /**
   * Loot eligibility (M7-B). Ordinary never drop equipment.
   * Defaults to ordinary when missing on legacy data.
   */
  classification?: EnemyClassification;
  /** Weighted intention pool; rolled each turn with seeded RNG. */
  intentions: Array<{ weight: number; intention: EnemyIntention }>;
}

/**
 * Placeholder enemy templates for core-combat encounters.
 * Stats are provisional and marked for later balance passes.
 */
export const ENEMY_TEMPLATES: Record<string, EnemyTemplate> = {
  // Provisional HP/damage — tuned so a focused Practice fight can win;
  // OPEN: final encounter balance.
  slime: {
    id: 'slime',
    name: 'Slime',
    maxHp: 12,
    classification: 'ordinary',
    intentions: [
      {
        weight: 3,
        intention: {
          kind: 'strike',
          label: 'Strike 3',
          damage: 3,
          guardAmount: 0,
        },
      },
      {
        weight: 1,
        intention: {
          kind: 'guard',
          label: 'Guard 2',
          damage: 0,
          guardAmount: 2,
        },
      },
    ],
  },
  goblin: {
    id: 'goblin',
    name: 'Goblin',
    maxHp: 10,
    classification: 'ordinary',
    intentions: [
      {
        weight: 2,
        intention: {
          kind: 'strike',
          label: 'Strike 4',
          damage: 4,
          guardAmount: 0,
        },
      },
      {
        weight: 1,
        intention: {
          kind: 'strike',
          label: 'Strike 2',
          damage: 2,
          guardAmount: 0,
        },
      },
      {
        weight: 1,
        intention: {
          kind: 'guard',
          label: 'Guard 2',
          damage: 0,
          guardAmount: 2,
        },
      },
    ],
  },
  bat: {
    id: 'bat',
    name: 'Cave Bat',
    maxHp: 6,
    classification: 'ordinary',
    intentions: [
      {
        weight: 3,
        intention: {
          kind: 'strike',
          label: 'Strike 2',
          damage: 2,
          guardAmount: 0,
        },
      },
      {
        weight: 1,
        intention: {
          kind: 'strike',
          label: 'Strike 4',
          damage: 4,
          guardAmount: 0,
        },
      },
    ],
  },
  /**
   * Explicit elite template for M7-B loot eligibility tests / proto cave.
   * Stats provisional — not inferred from “elite” naming alone; classification field is authoritative.
   */
  elite_goblin: {
    id: 'elite_goblin',
    name: 'Elite Goblin',
    maxHp: 22,
    classification: 'elite',
    intentions: [
      {
        weight: 2,
        intention: {
          kind: 'strike',
          label: 'Strike 5',
          damage: 5,
          guardAmount: 0,
        },
      },
      {
        weight: 1,
        intention: {
          kind: 'strike',
          label: 'Strike 3',
          damage: 3,
          guardAmount: 0,
        },
      },
      {
        weight: 1,
        intention: {
          kind: 'guard',
          label: 'Guard 3',
          damage: 0,
          guardAmount: 3,
        },
      },
    ],
  },
  /**
   * Final boss for the proto mini-dungeon.
   * Higher HP + ≥1 telegraphed special. OPEN: boss phases / balance.
   */
  cave_brute: {
    id: 'cave_brute',
    name: 'Cave Brute',
    maxHp: 48,
    classification: 'boss',
    intentions: [
      {
        weight: 3,
        intention: {
          kind: 'strike',
          label: 'Slam 6',
          damage: 6,
          guardAmount: 0,
        },
      },
      {
        weight: 2,
        intention: {
          kind: 'guard',
          label: 'Brace 4',
          damage: 0,
          guardAmount: 4,
        },
      },
      {
        weight: 2,
        intention: {
          kind: 'special',
          label: '⚠ Boulder Toss 10',
          damage: 10,
          guardAmount: 0,
        },
      },
    ],
  },
};

/** Default multi-enemy encounter for the Milestone 2 prototype. */
export const DEFAULT_ENCOUNTER_TEMPLATE_IDS = ['slime', 'goblin'] as const;

export function getEnemyClassification(
  template: EnemyTemplate,
): EnemyClassification {
  return template.classification ?? 'ordinary';
}
